<script setup>
import { ref, computed, watch } from 'vue';
import ReactionPicker from './ReactionPicker.vue';
import ReviewGalleryModal from './ReviewGalleryModal.vue';

const props = defineProps({
  review: {
    type: Object,
    required: true,
  },
  currentUserId: {
    type: [Number, String],
    default: null,
  },
});

const emit = defineEmits(['react', 'delete-review', 'filter-tag', 'filter-shop']);

const auth = useAuthStore();
const { addToast } = useToast();
const { fetchComments, addComment, deleteComment } = useReviewApi();

// UI States
const showReactionPicker = ref(false);
const showComments = ref(false);
const comments = ref([]);
const isCommentsLoading = ref(false);
const newCommentText = ref('');
const isSubmittingComment = ref(false);
const localCommentsCount = ref(props.review.stats?.comments_count || 0);

// Fullscreen Gallery Modal State
const isGalleryOpen = ref(false);
const galleryInitialIndex = ref(0);

const openGallery = (index = 0) => {
  galleryInitialIndex.value = index;
  isGalleryOpen.value = true;
};

watch(
  () => props.review.stats?.comments_count,
  (val) => {
    if (val !== undefined) localCommentsCount.value = val;
  }
);

// Author Check
const isAuthor = computed(() => {
  if (!props.currentUserId || !props.review.user) return false;
  return Number(props.currentUserId) === Number(props.review.user.id);
});

// Address link
const mapUrl = computed(() => {
  if (!props.review.address) return null;
  if (props.review.address.startsWith('http://') || props.review.address.startsWith('https://')) {
    return props.review.address;
  }
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(props.review.address)}`;
});

// Reaction mapping
const reactionConfig = {
  like: { label: 'Like', emoji: '👍', color: 'text-blue-600 font-semibold' },
  love: { label: 'Love', emoji: '❤️', color: 'text-rose-600 font-semibold' },
  care: { label: 'Care', emoji: '🥰', color: 'text-amber-600 font-semibold' },
  haha: { label: 'Haha', emoji: '😆', color: 'text-yellow-600 font-semibold' },
  wow: { label: 'Wow', emoji: '😮', color: 'text-yellow-600 font-semibold' },
  sad: { label: 'Sad', emoji: '😢', color: 'text-sky-600 font-semibold' },
  angry: { label: 'Angry', emoji: '😡', color: 'text-red-600 font-semibold' },
};

const currentReactionMeta = computed(() => {
  if (!props.review.current_user_reaction) return null;
  return reactionConfig[props.review.current_user_reaction] || null;
});

// Top active reactions for summary badge
const activeReactionBreakdown = computed(() => {
  const breakdown = props.review.stats?.breakdown || {};
  const active = [];
  for (const [type, count] of Object.entries(breakdown)) {
    if (count > 0 && reactionConfig[type]) {
      active.push({ type, count, emoji: reactionConfig[type].emoji });
    }
  }
  return active.sort((a, b) => b.count - a.count).slice(0, 3);
});

// Handle Reaction Click
const handleQuickReaction = () => {
  if (!auth.isAuthenticated) {
    addToast('Please login to react to reviews', 'alert-warning');
    navigateTo('/login');
    return;
  }
  if (props.review.current_user_reaction) {
    // Toggle off
    emit('react', { reviewId: props.review.id, type: 'none' });
  } else {
    // Default like
    emit('react', { reviewId: props.review.id, type: 'like' });
  }
};

const handleSelectReaction = (type) => {
  showReactionPicker.value = false;
  if (!auth.isAuthenticated) {
    addToast('Please login to react to reviews', 'alert-warning');
    navigateTo('/login');
    return;
  }
  emit('react', { reviewId: props.review.id, type });
};

// Comments Management
const toggleComments = async () => {
  showComments.value = !showComments.value;
  if (showComments.value && comments.value.length === 0) {
    await loadComments();
  }
};

const loadComments = async () => {
  isCommentsLoading.value = true;
  try {
    const res = await fetchComments(props.review.id);
    comments.value = res.data || [];
  } catch {
    addToast('Failed to load comments', 'alert-error');
  } finally {
    isCommentsLoading.value = false;
  }
};

const handleAddComment = async () => {
  if (!newCommentText.value.trim()) return;
  if (!auth.isAuthenticated) {
    addToast('Please login to comment', 'alert-warning');
    navigateTo('/login');
    return;
  }

  isSubmittingComment.value = true;
  try {
    const res = await addComment(props.review.id, newCommentText.value.trim());
    if (res.data) {
      comments.value.push(res.data);
      localCommentsCount.value++;
      newCommentText.value = '';
      addToast('Comment posted', 'alert-success');
    }
  } catch (err) {
    addToast(err.data?.message || 'Failed to post comment', 'alert-error');
  } finally {
    isSubmittingComment.value = false;
  }
};

const handleDeleteComment = async (commentId) => {
  try {
    await deleteComment(props.review.id, commentId);
    comments.value = comments.value.filter((c) => c.id !== commentId);
    if (localCommentsCount.value > 0) {
      localCommentsCount.value--;
    }
    addToast('Comment deleted', 'alert-success');
  } catch (err) {
    addToast(err.data?.message || 'Failed to delete comment', 'alert-error');
  }
};

const confirmDeleteReview = () => {
  if (confirm('Are you sure you want to delete this food review?')) {
    emit('delete-review', props.review.id);
  }
};

// Share review
const handleShare = async () => {
  const shareData = {
    title: `${props.review.restaurant_name} - Food Review`,
    text: props.review.details,
    url: window.location.href,
  };
  if (navigator.share) {
    try {
      await navigator.share(shareData);
    } catch {
      // User cancelled
    }
  } else {
    await navigator.clipboard.writeText(window.location.href);
    addToast('Review link copied to clipboard!', 'alert-success');
  }
};
</script>

<template>
  <div class="card bg-base-100 shadow-md sm:shadow-lg border border-base-200/80 rounded-2xl sm:rounded-3xl overflow-hidden hover:shadow-xl transition-all duration-300">
    <!-- Header: Author, Rating, Date & Options Menu -->
    <div class="p-3.5 sm:p-5 pb-2.5 sm:pb-3">
      <div class="flex items-start justify-between gap-2.5">
        <!-- Author Info -->
        <div class="flex items-center gap-2.5 sm:gap-3 min-w-0 flex-1">
          <div class="avatar placeholder shrink-0">
            <div class="bg-gradient-to-tr from-primary to-accent text-primary-content rounded-full w-9 h-9 sm:w-11 sm:h-11 ring-2 ring-primary/20 ring-offset-2 flex items-center justify-center text-center">
              <span class="text-base sm:text-lg md:text-xl font-extrabold uppercase select-none leading-none text-center">{{ review.user?.name ? review.user.name.charAt(0) : 'U' }}</span>
            </div>
          </div>
          <div class="min-w-0 flex-1">
            <h3 class="font-bold text-sm sm:text-base text-base-content leading-tight hover:text-primary transition-colors truncate">
              {{ review.user?.name || 'Anonymous Foodie' }}
            </h3>
            <div class="flex flex-wrap items-center gap-1.5 text-[11px] sm:text-xs text-base-content/60 mt-0.5">
              <span>{{ review.created_at || 'Recently' }}</span>
              <span>•</span>
              <span class="inline-flex items-center gap-0.5 font-semibold text-amber-500 bg-amber-500/10 px-1.5 py-0.5 rounded-md">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-3 h-3 sm:w-3.5 sm:h-3.5">
                  <path fill-rule="evenodd" d="M10.868 2.884c-.321-.772-1.415-.772-1.736 0l-1.83 4.401-4.753.381c-.833.067-1.171 1.107-.536 1.651l3.62 3.102-1.106 4.637c-.194.813.691 1.456 1.405 1.02L10 15.591l4.069 2.485c.713.436 1.598-.207 1.404-1.02l-1.106-4.637 3.62-3.102c.635-.544.297-1.584-.536-1.65l-4.752-.382-1.831-4.401Z" clip-rule="evenodd" />
                </svg>
                {{ review.rating ? review.rating + '/5' : 'Reviewed' }}
              </span>
            </div>
          </div>
        </div>

        <!-- Options Menu / Share / Delete -->
        <div class="dropdown dropdown-end shrink-0">
          <label tabindex="0" class="btn btn-ghost btn-circle btn-xs sm:btn-sm text-base-content/70">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4.5 w-4.5 sm:h-5 sm:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
            </svg>
          </label>
          <ul tabindex="0" class="dropdown-content z-30 menu p-1.5 sm:p-2 shadow-2xl bg-base-100 rounded-2xl w-40 sm:w-44 border border-base-200 text-xs sm:text-sm">
            <li>
              <button type="button" class="rounded-xl py-2" @click="handleShare">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" /></svg>
                Share
              </button>
            </li>
            <li v-if="isAuthor">
              <button type="button" class="text-error rounded-xl py-2" @click="confirmDeleteReview">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                Delete Review
              </button>
            </li>
          </ul>
        </div>
      </div>

      <!-- Restaurant & Location Info Box -->
      <div class="mt-2.5 sm:mt-3 p-2.5 sm:p-3 bg-base-200/50 hover:bg-base-200/80 rounded-xl sm:rounded-2xl transition-colors border border-base-200/40 flex items-center justify-between gap-2">
        <div class="flex items-center gap-2 sm:gap-2.5 min-w-0 flex-1">
          <div class="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 sm:h-4.5 sm:w-4.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <div class="min-w-0 flex-1">
            <div class="font-bold text-xs sm:text-sm text-base-content truncate">{{ review.restaurant_name || 'Restaurant' }}</div>
            <a
              v-if="mapUrl"
              :href="mapUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="text-[11px] sm:text-xs text-primary hover:underline flex items-center gap-1 truncate"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span class="truncate">{{ review.address }}</span>
            </a>
          </div>
        </div>

        <!-- Rating Stars -->
        <div v-if="review.rating" class="flex text-amber-400 shrink-0 text-xs sm:text-sm">
          <span v-for="s in 5" :key="s">
            {{ s <= review.rating ? '★' : '☆' }}
          </span>
        </div>
      </div>
    </div>

    <!-- Review Text Content -->
    <div class="px-3.5 sm:px-5 pb-3">
      <p class="text-xs sm:text-sm md:text-base text-base-content/90 whitespace-pre-wrap break-words leading-relaxed">
        {{ review.details }}
      </p>

      <!-- Tags Pills -->
      <div v-if="review.tags && review.tags.length" class="flex flex-wrap gap-1.5 mt-2.5 sm:mt-3">
        <button
          v-for="tag in review.tags"
          :key="tag"
          type="button"
          class="badge badge-sm badge-outline badge-primary hover:badge-primary transition-colors cursor-pointer text-[11px] sm:text-xs py-2 px-2.5 rounded-lg"
          @click="emit('filter-tag', tag)"
        >
          #{{ tag }}
        </button>
      </div>
    </div>

    <!-- Image Gallery Collage (Click opens Instagram-style Fullscreen Gallery) -->
    <div v-if="review.images && review.images.length" class="relative w-full bg-base-200/50 overflow-hidden select-none">
      
      <!-- Multi-photo Indicator Badge (Instagram Style) -->
      <div
        v-if="review.images.length > 1"
        class="absolute top-2.5 right-2.5 z-10 px-2.5 py-1 bg-black/60 backdrop-blur-md rounded-full text-white text-[11px] font-semibold flex items-center gap-1.5 shadow-md pointer-events-none"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <span>{{ review.images.length }} Photos</span>
      </div>

      <!-- 1 Image -->
      <div
        v-if="review.images.length === 1"
        class="h-60 sm:h-72 md:h-80 lg:h-96 w-full cursor-pointer overflow-hidden group relative"
        @click="openGallery(0)"
      >
        <img
          :src="review.images[0].url || review.images[0]"
          alt="Food review photo"
          class="w-full h-full object-cover group-hover:scale-103 transition-transform duration-500"
          loading="lazy"
        >
        <div class="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
      </div>

      <!-- 2 Images -->
      <div v-else-if="review.images.length === 2" class="grid grid-cols-2 gap-1 h-48 sm:h-64 md:h-72 lg:h-80">
        <div
          v-for="(img, idx) in review.images"
          :key="idx"
          class="h-full cursor-pointer overflow-hidden group relative"
          @click="openGallery(idx)"
        >
          <img
            :src="img.url || img"
            alt="Food review photo"
            class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          >
          <div class="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
        </div>
      </div>

      <!-- 3+ Images Collage (Clicking any photo opens full carousel) -->
      <div v-else class="grid grid-cols-2 gap-1 h-60 sm:h-72 md:h-80 lg:h-96">
        <!-- Main Large Photo (Index 0) -->
        <div
          class="h-full cursor-pointer overflow-hidden group relative"
          @click="openGallery(0)"
        >
          <img
            :src="review.images[0].url || review.images[0]"
            alt="Food photo"
            class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          >
          <div class="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
        </div>

        <!-- Right 2-grid (Index 1 & Index 2) -->
        <div class="grid grid-rows-2 gap-1 h-full">
          <div
            class="h-full cursor-pointer overflow-hidden group relative"
            @click="openGallery(1)"
          >
            <img
              :src="review.images[1].url || review.images[1]"
              alt="Food photo"
              class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              loading="lazy"
            >
            <div class="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
          </div>

          <div
            class="relative h-full cursor-pointer overflow-hidden group"
            @click="openGallery(2)"
          >
            <img
              :src="review.images[2].url || review.images[2]"
              alt="Food photo"
              class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              loading="lazy"
            >
            <!-- +N Overlay for remaining images -->
            <div
              v-if="review.images.length > 3"
              class="absolute inset-0 bg-black/60 backdrop-blur-xs flex flex-col items-center justify-center text-white font-bold transition-colors group-hover:bg-black/70"
            >
              <span class="text-xl sm:text-2xl">+{{ review.images.length - 3 }}</span>
              <span class="text-[10px] sm:text-xs font-normal opacity-90">View all</span>
            </div>
            <div v-else class="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
          </div>
        </div>
      </div>
    </div>

    <!-- Reaction & Comment Summary Counters -->
    <div class="px-3.5 sm:px-5 py-2 sm:py-2.5 flex items-center justify-between text-xs text-base-content/60 border-t border-base-200/80">
      <!-- Reaction Emojis & Total Count -->
      <div class="flex items-center gap-1.5">
        <div v-if="activeReactionBreakdown.length" class="flex -space-x-1">
          <span
            v-for="r in activeReactionBreakdown"
            :key="r.type"
            class="text-sm select-none"
            :title="`${r.type}: ${r.count}`"
          >
            {{ r.emoji }}
          </span>
        </div>
        <span v-if="review.stats?.total_reactions > 0" class="font-semibold text-base-content/80">
          {{ review.stats.total_reactions }} {{ review.stats.total_reactions === 1 ? 'reaction' : 'reactions' }}
        </span>
        <span v-else class="text-base-content/40 italic">Be the first to react</span>
      </div>

      <!-- Comments Count -->
      <div>
        <button type="button" class="hover:underline cursor-pointer font-medium hover:text-primary transition-colors" @click="toggleComments">
          {{ localCommentsCount }} {{ localCommentsCount === 1 ? 'comment' : 'comments' }}
        </button>
      </div>
    </div>

    <!-- Action Bar: React, Comment, Share -->
    <div class="px-2 sm:px-3 py-1 bg-base-100 border-t border-base-200/80 flex items-center justify-between gap-1 relative">
      <!-- Floating Reaction Picker Menu -->
      <div
        v-if="showReactionPicker"
        class="absolute -top-12 sm:-top-13 left-2 sm:left-4 z-40"
        @mouseleave="showReactionPicker = false"
      >
        <ReactionPicker @select="handleSelectReaction" />
      </div>

      <!-- React Button -->
      <div
        class="relative flex-1"
        @mouseenter="showReactionPicker = true"
      >
        <button
          type="button"
          class="btn btn-ghost btn-sm w-full gap-1.5 sm:gap-2 text-xs sm:text-sm font-semibold rounded-xl"
          :class="currentReactionMeta ? currentReactionMeta.color : 'text-base-content/70 hover:text-primary'"
          @click="handleQuickReaction"
        >
          <span class="text-base sm:text-lg">{{ currentReactionMeta ? currentReactionMeta.emoji : '👍' }}</span>
          <span>{{ currentReactionMeta ? currentReactionMeta.label : 'Like' }}</span>
        </button>
      </div>

      <!-- Comment Button -->
      <button
        type="button"
        class="btn btn-ghost btn-sm flex-1 gap-1.5 sm:gap-2 text-xs sm:text-sm font-semibold rounded-xl text-base-content/70 hover:text-primary"
        @click="toggleComments"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 sm:h-5 sm:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
        <span>Comment</span>
      </button>

      <!-- Share Button -->
      <button
        type="button"
        class="btn btn-ghost btn-sm flex-1 gap-1.5 sm:gap-2 text-xs sm:text-sm font-semibold rounded-xl text-base-content/70 hover:text-primary"
        @click="handleShare"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 sm:h-5 sm:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
        </svg>
        <span>Share</span>
      </button>
    </div>

    <!-- Comments Section Drawer -->
    <div v-if="showComments" class="px-3.5 sm:px-5 py-3.5 sm:py-4 bg-base-200/40 border-t border-base-200/80 transition-all">
      <!-- Loading State -->
      <div v-if="isCommentsLoading" class="flex justify-center py-4">
        <span class="loading loading-spinner loading-md text-primary" />
      </div>

      <!-- Comments List -->
      <div v-else class="space-y-2.5 sm:space-y-3 mb-3.5 sm:mb-4">
        <div
          v-for="comment in comments"
          :key="comment.id"
          class="flex items-start gap-2 sm:gap-2.5 group"
        >
          <!-- Commenter Avatar -->
          <div class="avatar placeholder shrink-0 mt-0.5">
            <div class="bg-primary/20 text-primary rounded-full w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center text-center">
              <span class="text-[11px] sm:text-xs font-bold uppercase select-none leading-none text-center">{{ comment.user?.name ? comment.user.name.charAt(0) : 'U' }}</span>
            </div>
          </div>

          <!-- Comment Bubble -->
          <div class="flex-1 min-w-0 bg-base-100 p-2.5 sm:p-3 rounded-2xl rounded-tl-none shadow-xs border border-base-200/80">
            <div class="flex items-center justify-between gap-1.5">
              <span class="font-bold text-xs text-base-content truncate">{{ comment.user?.name || 'User' }}</span>
              <span class="text-[10px] text-base-content/50 shrink-0">{{ comment.created_at || 'Just now' }}</span>
            </div>
            <p class="text-xs sm:text-sm text-base-content/90 mt-1 whitespace-pre-wrap break-words leading-relaxed">{{ comment.comment }}</p>
          </div>

          <!-- Delete Comment Action -->
          <button
            v-if="currentUserId && (Number(currentUserId) === Number(comment.user?.id) || isAuthor)"
            type="button"
            class="btn btn-ghost btn-circle btn-xs text-error shrink-0 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
            title="Delete comment"
            @click="handleDeleteComment(comment.id)"
          >
            ✕
          </button>
        </div>

        <div v-if="comments.length === 0" class="text-center text-xs text-base-content/50 py-3 italic">
          No comments yet. Be the first to share your thoughts!
        </div>
      </div>

      <!-- Add Comment Form -->
      <form class="flex items-center gap-2" @submit.prevent="handleAddComment">
        <div class="avatar placeholder shrink-0">
          <div class="bg-primary text-primary-content rounded-full w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center text-center">
            <span class="text-[11px] sm:text-xs font-bold uppercase select-none leading-none text-center">{{ auth.user?.name ? auth.user.name.charAt(0) : 'Me' }}</span>
          </div>
        </div>
        <div class="relative flex-1 min-w-0">
          <input
            v-model="newCommentText"
            type="text"
            placeholder="Write a comment..."
            maxlength="1000"
            class="input input-sm sm:input-md input-bordered w-full rounded-full pr-10 text-xs sm:text-sm focus:ring-2 ring-primary/20 bg-base-100"
            :disabled="isSubmittingComment"
          >
          <button
            type="submit"
            class="btn btn-primary btn-circle btn-xs sm:btn-sm absolute right-1.5 top-1/2 -translate-y-1/2 text-white"
            :disabled="!newCommentText.trim() || isSubmittingComment"
          >
            <span v-if="isSubmittingComment" class="loading loading-spinner loading-xs" />
            <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5 sm:h-4 sm:w-4" viewBox="0 0 20 20" fill="currentColor">
              <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
            </svg>
          </button>
        </div>
      </form>
    </div>

    <!-- Instagram-style Fullscreen Gallery Modal -->
    <ReviewGalleryModal
      :is-open="isGalleryOpen"
      :images="review.images"
      :initial-index="galleryInitialIndex"
      :review="review"
      @close="isGalleryOpen = false"
    />
  </div>
</template>
